import React, { useEffect, useState } from 'react';
import { alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import { styled } from '@mui/material/styles';
import DeleteIcon from '@mui/icons-material/Delete';
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import { visuallyHidden } from '@mui/utils';
import { AnsweredQuestions, Member, MemberReportInfo } from '@/utils/types';
import { TextField } from '@mui/material';
import { Cancel, Notifications } from '@mui/icons-material';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';
import ListItemText from '@mui/material/ListItemText';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { DownloadIcon } from 'lucide-react';
import { adminGetMemberInfo, adminGetMembersReportInfo } from '@/api';

interface Data {
  id: string;
  user: string;
  topPath: string;
  year: number | string;
  questionsCompleted: number;
  averageScore: number;
}

function createData(
  id: string,
  user: string,
  topPath: string,
  year: number | string,
  questionsCompleted: number,
  averageScore: number,
): Data {
  return {
    id,
    user,
    topPath,
    year,
    questionsCompleted,
    averageScore
  };
}

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

type Order = 'asc' | 'desc';

function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key,
): (
  a: { [key in Key]: number | string },
  b: { [key in Key]: number | string },
) => number {
  return order === 'desc'
    ? (a, b) => {
        const result = descendingComparator(a, b, orderBy);
        if (result === 0) {
          const userKey = "user" as Key;
          // If primary sort (membershipType) is equal, sort by user
          return -descendingComparator(a, b, userKey);
        }
        return result;
      }
    : (a, b) => {
        const result = -descendingComparator(a, b, orderBy);
        if (result === 0) {
          const userKey = "user" as Key;
          // If primary sort (membershipType) is equal, sort by user
          return -descendingComparator(a, b, userKey);
        }
        return result;
      };
}

interface HeadCell {
  disablePadding: boolean;
  id: keyof Data;
  label: string;
  numeric: boolean;
}


const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: '#9daec250',
    color: theme.palette.common.black,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  // hide last border
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

const headCells: readonly HeadCell[] = [
  {
    id: 'user',
    numeric: false,
    disablePadding: false,
    label: 'User'
  },
  {
    id: 'id',
    numeric: false,
    disablePadding: false,
    label: 'Email'
  },
  {
    id: 'topPath',
    numeric: false,
    disablePadding: false,
    label: 'Top Path'
  },
  {
    id: 'year',
    numeric: true,
    disablePadding: false,
    label: 'Year'
  },
  {
    id: 'questionsCompleted',
    numeric: true,
    disablePadding: false,
    label: 'Questions Completed'
  },
  {
    id: 'averageScore',
    numeric: true,
    disablePadding: false,
    label: 'Average Score'
  },
];

interface ReportsMemberTableHeadProps {
  onRequestSort: (event: React.MouseEvent<unknown>, property: keyof Data) => void;
  order: Order;
  orderBy: string;
  rowCount: number;
}

function ReportsMemberTableHead(props: ReportsMemberTableHeadProps) {
  const { order, orderBy, rowCount, onRequestSort } =
    props;
  const createSortHandler =
    (property: keyof Data) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };

  return (
    <TableHead>
      <TableRow>
        {headCells.filter(x => x.id != 'id').map((headCell) => (
          <StyledTableCell
            key={headCell.id}
            align={'left'}
            padding={headCell.disablePadding ? 'none' : 'normal'}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </Box>
              ) : null}
            </TableSortLabel>
          </StyledTableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}
interface ReportsMemberTableToolbarProps {
  filterBar: React.ReactElement;
  heading: string;
  downloadButton: React.ReactElement;
}
function ReportsMemberTableToolbar(props: ReportsMemberTableToolbarProps) {
  return (
    <Toolbar
      sx={[
        {
          pl: { sm: 2 },
          pr: { xs: 1, sm: 1 },
        },
      ]}
    >
      <Typography
        sx={{ flex: '1 1 100%', textAlign: 'left' }}
        variant="h6"
        id="tableTitle"
        component="div"
      >
        {props.heading}
      </Typography>
      <Tooltip title="Filter by year">
        {props.filterBar}
      </Tooltip>
      <Tooltip title="Download Report">
        {props.downloadButton}
      </Tooltip>
    </Toolbar>
  );
}

interface ReportsMemberTableProps {
  members: Member[];
  error?: boolean
  setoverlayComp: (val: boolean) => void
}

const formatDate = (date: string): string => {
  const dateNoTime = date.split(" ")[0];
  const splitDate = dateNoTime.split("-");
  return `${splitDate[1]}/${splitDate[2]}/${splitDate[0]}`
}

const calculateTotals = (data: Data[]) => {
  const totalUsers = data.length;
  const totalQuestionsCompleted = data.reduce((sum, row) => sum + row.questionsCompleted, 0);
  const totalAverageScore = data.reduce((sum, row) => sum + row.averageScore, 0) / data.length;

  const topPathCounts: { [key: string]: number } = {};
  data.forEach(row => {
    topPathCounts[row.topPath] = (topPathCounts[row.topPath] || 0) + 1;
  });
  const keys = Object.keys(topPathCounts);
  const mostRecurringTopPath = keys.length > 0? keys.reduce((a, b) => topPathCounts[a] > topPathCounts[b] ? a : b) : "";

  const distinctYears = new Set(data.map(row => row.year)).size;

  return { totalUsers, totalQuestionsCompleted, totalAverageScore, mostRecurringTopPath, distinctYears };
};

export default function ReportsMemberTable(props: ReportsMemberTableProps) {
  const [rows, setrows] = useState<Data[]>([]);
  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<keyof Data>('year');
  const [selected, setSelected] = useState<string>("");
  const [page, setPage] = useState(0);
  const [dense, setDense] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortedData, setSortedData] = useState<Data[]>(rows);
  const [membersInfo, setMembersInfo] = useState<MemberReportInfo[]>([]);
  const [loading, setloading] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedyearTypes, setSelectedyearTypes] = useState<number[]>([]);
  const [yearTypes, setyearTypes] = useState<number[]>([]);

  const handleFilterClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setAnchorEl(null);
  };

  const handleYearTypeChange = (type: number) => {
    const value = type;
    setSelectedyearTypes((prev) =>
      prev.includes(value) ? prev.filter((type) => type !== value) : [...prev, value]
    );
  };

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof Data,
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  useEffect(() => {
    if(props.members.length>0){
      const yearSet: Set<number> = new Set();
      props.members.forEach(mem => yearSet.add(mem.year));
      setyearTypes(Array.from(yearSet).filter(x=>x !== null));
      setloading(true);
      const fetchData = async () => {
          adminGetMembersReportInfo(props.members.map(x=>x.email)).then((obj) => {
            setMembersInfo(obj as MemberReportInfo[]??[]);
        })
      };
      fetchData();
    }
  }, [props.members]);

  useEffect(() => {
    const mapE: { [key: string]: Member } = {};
    props.members.forEach(mem => mapE[mem.email] = mem);

    const nrows = membersInfo.map(memInfo => createData(
      mapE[memInfo.email].email,
      mapE[memInfo.email].name,
      memInfo.topPath,
      mapE[memInfo.email].year,
      memInfo.interviewsCompleted,
      memInfo.avgInterviewScore
    ));

    setrows(nrows);
    setSortedData(nrows);
    setloading(false);
  }, [membersInfo]);
      
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - sortedData.length) : 0;

  const visibleRows = React.useMemo(
    () =>
      [...sortedData]
        .sort(getComparator(order, orderBy))
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [order, orderBy, page, rowsPerPage, sortedData],
  );

  const downloadPDF = () => {
    const doc = new jsPDF();
    doc.text('Members Report', 14, 16);
    const body = sortedData.map(row => [row.user, row.topPath, row.year, row.questionsCompleted, row.averageScore.toFixed(1)]);
    body.push(['', '', '', '', ''], [`Totals:    ${totalUsers}`, mostRecurringTopPath , distinctYears, totalQuestionsCompleted, totalAverageScore.toFixed(1)]);
    autoTable(doc, {
      head: [['User', 'Top Path', 'Year', 'Interviews Completed', 'Average Score']],
      body: body,
      startY: 20,
    });
    doc.save('members_report.pdf');
  };

  useEffect(() => {
      props.setoverlayComp(loading);
  }, [loading]);


  useEffect(() => {
    let filteredRows = rows.filter((row) =>
      selectedyearTypes.length === 0 || selectedyearTypes.includes(row.year as number)
    );
    setSortedData(filteredRows);
  }, [selectedyearTypes]);

  const { totalUsers, totalQuestionsCompleted, totalAverageScore, mostRecurringTopPath, distinctYears } = calculateTotals(sortedData);

  return (
    <Box sx={{ width: '100%' }}>
      <Paper sx={{ width: '100%', mb: 2 }}>
        <ReportsMemberTableToolbar
          heading={"Active Students"}
          filterBar={
            <>
              <IconButton
                aria-label="filter list"
                aria-controls="filter-menu"
                aria-haspopup="true"
                onClick={handleFilterClick}
              >
                <FilterListIcon />
              </IconButton>
              <Menu
                id="filter-menu"
                anchorEl={anchorEl}
                keepMounted
                open={Boolean(anchorEl)}
                onClose={handleFilterClose}
              >
                {yearTypes.map((type) => (
                  <div className="flex px-2 items-center justify-center" onClick={() => handleYearTypeChange(type)} key={type}>
                    <Checkbox
                      checked={selectedyearTypes.includes(type)}
                      value={type}
                    />
                    <ListItemText primary={type} />
                  </div>
                ))}
              </Menu>
            </>
          }
          downloadButton={
            <IconButton onClick={downloadPDF}>
              <DownloadIcon />
            </IconButton>
          }
        />
        <TableContainer>
          <Table
            sx={{ minWidth: 750 }}
            aria-labelledby="tableTitle"
            size={dense ? 'small' : 'medium'}
          >
            <ReportsMemberTableHead
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              rowCount={sortedData.length}
            />
            <TableBody>
              {visibleRows.map((row, index) => {
                const isItemSelected = selected === row.id;
                return (
                  <StyledTableRow
                    role="checkbox"
                    tabIndex={-1}
                    key={row.id}
                    title={row.id}
                    selected={isItemSelected}
                    sx={{ cursor: 'pointer' }}
                  >
                    <StyledTableCell align="left">{row.user}</StyledTableCell>
                    <StyledTableCell align="left">{row.topPath}</StyledTableCell>
                    <StyledTableCell align="left">{row.year}</StyledTableCell>
                    <StyledTableCell align="left">{row.questionsCompleted}</StyledTableCell>
                    <StyledTableCell align="left">{row.averageScore.toFixed(1)}</StyledTableCell>
                  </StyledTableRow>
                );
              })}
              {emptyRows > 0 && (
                <TableRow
                  style={{
                    height: (dense ? 33 : 53) * emptyRows,
                  }}
                >
                  <StyledTableCell colSpan={6} />
                </TableRow>
              )}
              <TableRow>
              <StyledTableCell colSpan={6} />
              </TableRow>
              <TableRow>
                <StyledTableCell align="left" sx={{ fontWeight: 'bold' }}>
                  Totals:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;{totalUsers}
                </StyledTableCell>
                <StyledTableCell align="left" sx={{ fontWeight: 'bold' }}>{mostRecurringTopPath}</StyledTableCell>
                <StyledTableCell align="left" sx={{ fontWeight: 'bold' }}>{distinctYears}</StyledTableCell>
                <StyledTableCell align="left" sx={{ fontWeight: 'bold' }}>{totalQuestionsCompleted}</StyledTableCell>
                <StyledTableCell align="left" sx={{ fontWeight: 'bold' }}>{totalAverageScore.toFixed(1)}</StyledTableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={sortedData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </Box>
  );
}